import React, { useContext, useState, useEffect } from 'react';
import { 
  Typography,
  Container,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

export default function NewUser() {
  const { user, tokens, authData } = useContext(AuthContext);
  const { phase3 } = useFeatureFlags();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';

  // EULA dialog state
  const [eulaOpen, setEulaOpen] = useState(false);
  const [eulaUrl, setEulaUrl] = useState(null);
  const [eulaLoading, setEulaLoading] = useState(false);
  const [eulaError, setEulaError] = useState('');
  const [pendingRole, setPendingRole] = useState(null);

  // Invitation-related state (always initialized)
  const [invitations, setInvitations] = useState(authData?.invitations || []);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  // Sync authData invitations whenever authData changes
  useEffect(() => {
    setInvitations(authData?.invitations || []);
  }, [authData]);

  if (!user) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setOpenDialog(true);
  };

  // Confirm and set the user role via API (roleToSet overrides selectedRole)
  const handleConfirm = async (roleToSet) => {
    const role = roleToSet || selectedRole;
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/users/${user.id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.id_token}`,
        },
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        throw new Error('Failed to update role');
      }

      // Navigate to root path and then reload
      window.location.href = '/';
    } catch (error) {
      console.error('Error updating role:', error);
      // TODO: Show error message to user
    } finally {
      setLoading(false);
      setOpenDialog(false);
    }
  };

  // Open EULA dialog and load PDF for agreement key 'eula'
  const openEula = async (roleToSet) => {
    setPendingRole(roleToSet);
    setEulaError('');
    setEulaLoading(true);
    setEulaOpen(true);
    try {
      const res = await fetch(`${apiBase}/agreements/key/eula/document`, {
        headers: { 'Authorization': `Bearer ${tokens.id_token}` },
      });
      if (!res.ok) throw new Error('Failed to load EULA');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setEulaUrl(url);
    } catch (err) {
      console.error(err);
      setEulaError(err.message || 'Failed to load EULA');
    } finally {
      setEulaLoading(false);
    }
  };

  const closeEula = () => {
    setEulaOpen(false);
    if (eulaUrl) {
      try { window.URL.revokeObjectURL(eulaUrl); } catch {}
    }
    setEulaUrl(null);
    setPendingRole(null);
  };

  const agreeEulaAndContinue = async () => {
    if (!pendingRole) { closeEula(); return; }
    try {
      setEulaLoading(true);
      setEulaError('');
      // Record agreement for current user
      const res = await fetch(`${apiBase}/agreements/agree/user/eula`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tokens.id_token}` },
      });
      if (!res.ok) throw new Error('Failed to record agreement');
      // Proceed with setting the role
      closeEula();
      await handleConfirm(pendingRole);
    } catch (err) {
      console.error(err);
      setEulaError(err.message || 'Failed to record agreement');
    } finally {
      setEulaLoading(false);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedRole(null);
  };

  
  const handleAcceptClick = (inv) => {
    setSelectedInvitation(inv);
    setAcceptDialogOpen(true);
  };

  const handleAcceptConfirm = async () => {
    if (!selectedInvitation) return;
    setAcceptLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/users/${user.id}/invitation`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.id_token}`,
          },
          body: JSON.stringify({ ...selectedInvitation, status: 'Accepted' }),
        }
      );
      if (!res.ok) throw new Error('Failed to accept invitation');
      // Reload the application on successful acceptance
      window.location.href = '/';
    } catch (error) {
      console.error(error);
    } finally {
      setAcceptLoading(false);
      setAcceptDialogOpen(false);
      setSelectedInvitation(null);
    }
  };
  

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      {/* Greeting */}
      <Typography variant="h4" component="h1" gutterBottom sx={{ textTransform: 'none' }}>
        Welcome, {user.name || user.email}
      </Typography>
      {/* Show invitation cards if any, else show enrollment card */}
      {invitations.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          {invitations.map((inv) => (
            <Card key={inv.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Invitation</Typography>
                <Typography>
                  You are invited to <strong>{inv.organization_name}</strong> as a <strong>{inv.role}</strong>.
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={() => handleAcceptClick(inv)}>
                  Accept
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="body1" sx={{ textTransform: 'none' }}>
              Your next step is to fill out a short application to enroll as a verified VeraBIND provider. Please select your role and get started.
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'center', gap: 2, py: 2 }}>
            {phase3 && (
              <Button
                variant="contained"
                size="large"
                onClick={() => handleRoleSelect('Patient')}
                disabled={loading}
              >
                I Am a Patient
              </Button>
            )}
              <Button
                variant="contained"
                size="large"
                onClick={() => { setSelectedRole(null); setOpenDialog(true); }}
                disabled={loading}
              >
              Get Started
              </Button>
          </CardActions>
        </Card>
      )}

      <Dialog open={openDialog} onClose={handleClose}>
        {selectedRole === 'Patient' ? (
          <>
            <DialogTitle>Confirm role selection</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ textTransform: 'none' }}>
                You are selecting the Patient role. This will allow you to view your lab results. Are you sure?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={() => openEula('Patient')} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Confirm'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Select your role</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ textTransform: 'none' }}>
                Please select your role to get started:
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              {/* Cancel on left */}
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              {/* Role options on right; open EULA before setting role */}
              <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                <Button variant="contained" size="large" onClick={() => openEula('PendingProvider')} disabled={loading}>
                  Provider
                </Button>
                <Button variant="contained" size="large" onClick={() => openEula('PendingOfficeStaff')} disabled={loading}>
                  Office Staff
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* EULA Dialog */}
      <Dialog open={eulaOpen} onClose={() => !eulaLoading && closeEula()} maxWidth="md" fullWidth>
        <DialogTitle>End User License Agreement</DialogTitle>
        <DialogContent dividers sx={{ height: 600 }}>
          {eulaError && (
            <Typography color="error" sx={{ mb: 2 }}>Error: {eulaError}</Typography>
          )}
          {eulaLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
          {!eulaLoading && eulaUrl && (
            <embed src={eulaUrl} type="application/pdf" width="100%" height="100%" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEula} disabled={eulaLoading}>Cancel</Button>
          <Button variant="contained" onClick={agreeEulaAndContinue} disabled={eulaLoading || !!eulaError}>
            {eulaLoading ? <CircularProgress size={24} /> : 'Agree'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Accept Invitation Confirmation */}
      <Dialog open={acceptDialogOpen} onClose={() => !acceptLoading && setAcceptDialogOpen(false)}>
        <DialogTitle>Accept invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to accept the invitation to <strong>{selectedInvitation?.organization_name}</strong> as <strong>{selectedInvitation?.role}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)} disabled={acceptLoading}>
            Cancel
          </Button>
          <Button onClick={handleAcceptConfirm} disabled={acceptLoading}>
            {acceptLoading ? <CircularProgress size={24}/> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 
